import { useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { MeetingProgress } from './MeetingProgress';
import { TranscriptionPanel } from './TranscriptionPanel';
import { SummaryPanel } from './SummaryPanel';
import { formatBytes, formatDate, formatDuration } from '../lib/audio';
import { formatModelLabel, SUMMARY_MODELS } from '../lib/models';

interface Props {
  meetingId: Id<'meetings'>;
  onBack: () => void;
}

export function MeetingDetail({ meetingId, onBack }: Props) {
  const data = useQuery(api.meetings.get, { meetingId });
  const retryMeeting = useMutation(api.meetings.retry);
  const regenerateSummary = useMutation(api.meetings.regenerateSummary);
  const [rerunModel, setRerunModel] = useState<string | null>(null);
  const [rerunBusy, setRerunBusy] = useState(false);

  if (data === undefined) {
    return (
      <div className="term-box p-12 text-center relative z-10">
        <div className="spinner-tech mx-auto mb-3" />
        <p className="font-mono text-xs text-[var(--color-asphalt-500)]">$ fetching record…</p>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="term-box p-8 relative z-10">
        <p className="font-mono text-xs text-[var(--color-burgundy)]">[err 404] record not found</p>
        <button type="button" onClick={onBack} className="term-link mt-4">
          ← cd ..
        </button>
      </div>
    );
  }

  const { meeting, chunks } = data;

  const canRegenerateSummary =
    meeting.chunksCompleted === meeting.chunkCount &&
    meeting.chunkCount > 0 &&
    meeting.status !== 'pending' &&
    meeting.status !== 'transcribing' &&
    meeting.status !== 'summarizing';
  const selectedRerunModel = rerunModel ?? meeting.summaryModel;

  const onRegenerateSummary = async () => {
    if (
      !window.confirm(
        `¿Regenerar el summary de "${meeting.summary?.title || meeting.fileName}" con ${formatModelLabel(selectedRerunModel)}?`
      )
    )
      return;
    setRerunBusy(true);
    try {
      await regenerateSummary({ meetingId, summaryModel: selectedRerunModel });
    } finally {
      setRerunBusy(false);
    }
  };

  return (
    <div className="space-y-12 sm:space-y-16">
      <div className="reveal-up">
        <button type="button" onClick={onBack} className="term-link mb-6 no-print">
          ← cd ../history
        </button>

        <p className="label-mono mb-3">
          <span className="text-[var(--color-amber-mid)]">↳</span> cat /records/$id
        </p>
        <h2 className="heading-display text-3xl sm:text-4xl mb-3 break-words">
          {meeting.summary?.title || meeting.fileName}
        </h2>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[11px] text-[var(--color-asphalt-400)] nums-tabular min-w-0">
          <span>{formatDate(meeting._creationTime)}</span>
          <span className="text-[var(--color-asphalt-300)]">│</span>
          <span className="truncate max-w-full sm:max-w-xs break-words">{meeting.fileName}</span>
          <span className="text-[var(--color-asphalt-300)]">│</span>
          <span>{formatBytes(meeting.fileSizeBytes)}</span>
          {meeting.durationSec != null && (
            <>
              <span className="text-[var(--color-asphalt-300)]">│</span>
              <span>{formatDuration(meeting.durationSec)}</span>
            </>
          )}
          {meeting.chunkCount > 1 && (
            <>
              <span className="text-[var(--color-asphalt-300)]">│</span>
              <span>{meeting.chunkCount} chunks</span>
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          <span className="term-chip">
            stt <b>{formatModelLabel(meeting.transcriptionModel)}</b>
          </span>
          <span className="term-chip">
            sum <b>{formatModelLabel(meeting.summaryModel)}</b>
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-5 no-print">
          {meeting.status !== 'done' && (
            <button
              type="button"
              onClick={() => retryMeeting({ meetingId })}
              className="term-link"
            >
              $ retry --process
            </button>
          )}
          {canRegenerateSummary && (
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={selectedRerunModel}
                onChange={(e) => setRerunModel(e.target.value)}
                disabled={rerunBusy}
                className="history-model-select"
                aria-label="Modelo para reprocesar summary"
              >
                {SUMMARY_MODELS.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => void onRegenerateSummary()}
                disabled={rerunBusy}
                className="term-link"
              >
                {rerunBusy ? '$ summary…' : '$ summary --rerun'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Progreso si todavía está corriendo */}
      <MeetingProgress
        clientStage="submitted"
        meeting={meeting}
        chunks={chunks}
      />

      {/* Resultados si están disponibles */}
      {meeting.transcript && (
        <TranscriptionPanel transcript={meeting.transcript} audioFileName={meeting.fileName} />
      )}

      {meeting.summary && meeting.status === 'done' && (
        <SummaryPanel summary={meeting.summary} audioFileName={meeting.fileName} />
      )}
    </div>
  );
}
